import type {
  ConferenceIndex,
  HighlightColor,
  StudyData,
  ThreadsIndex,
} from '../../data/schema';
import { allThreads } from '../../data/threads';
import { compactVerseSelection } from '../scriptures/sciLinks';
import { buildStudyMarkdown } from './exportMarkdown';

const HIGHLIGHT_LABELS: Record<HighlightColor, string> = {
  yellow: 'Key insights',
  blue: 'Scripture and doctrine',
  green: 'Applications and actions',
  pink: 'Quotes to remember',
};

function routerLink(href: string, label: string): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = href;
  link.dataset.router = '';
  link.textContent = label;
  return link;
}

function section(titleText: string): {
  section: HTMLElement;
  body: HTMLElement;
} {
  const wrapper = document.createElement('section');
  wrapper.className = 'study-section card';
  const title = document.createElement('h2');
  title.textContent = titleText;
  const body = document.createElement('div');
  body.className = 'study-section__body';
  wrapper.append(title, body);
  return { section: wrapper, body };
}

function empty(message: string): HTMLParagraphElement {
  const paragraph = document.createElement('p');
  paragraph.className = 'muted';
  paragraph.textContent = message;
  return paragraph;
}

export function createStudySignIn(): HTMLElement {
  const page = document.createElement('div');
  page.className = 'study-page page-width';
  page.dataset.route = 'study';
  const panel = document.createElement('section');
  panel.className = 'study-sign-in card';
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow eyebrow--gold';
  eyebrow.textContent = 'Private study';
  const heading = document.createElement('h1');
  heading.textContent = 'Sign in to open My Study';
  const copy = document.createElement('p');
  copy.textContent =
    'Your V2 highlights, notes, bookmarks, progress, and explored scriptures are private to your account.';
  const link = document.createElement('a');
  link.href = '/auth/google';
  link.className = 'button';
  link.textContent = 'Sign in with Google';
  panel.append(eyebrow, heading, copy, link);
  page.append(panel);
  return page;
}

export function createStudyPage(
  data: StudyData,
  index: ConferenceIndex,
  threads: ThreadsIndex,
): HTMLElement {
  const page = document.createElement('div');
  page.className = 'study-page page-width';
  page.dataset.route = 'study';
  const talkById = new Map(index.talks.map((talk) => [talk.talkId, talk]));
  const threadById = new Map(
    allThreads(threads).map((thread) => [thread.threadId, thread]),
  );

  const header = document.createElement('header');
  header.className = 'study-heading';
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow eyebrow--gold';
  eyebrow.textContent = 'My Study';
  const title = document.createElement('h1');
  title.textContent = 'Your conference journey';
  const copy = document.createElement('p');
  copy.textContent =
    'Everything saved here belongs to your signed-in account and starts fresh in V2.';
  const exportButton = document.createElement('button');
  exportButton.type = 'button';
  exportButton.className = 'button';
  exportButton.textContent = 'Export Markdown';
  exportButton.addEventListener('click', () => {
    const markdown = buildStudyMarkdown(data, index, threads);
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const href = URL.createObjectURL(blob);
    const download = document.createElement('a');
    download.href = href;
    download.download = `conference-study-${index.conferenceId}.md`;
    download.click();
    URL.revokeObjectURL(href);
  });
  header.append(eyebrow, title, copy, exportButton);

  const stats = document.createElement('section');
  stats.className = 'study-stats';
  stats.setAttribute('aria-label', 'Study journey statistics');
  const measures: Array<[string, string]> = [
    [
      String(
        data.readingProgress.filter((progress) => progress.completed).length,
      ),
      'Talks completed',
    ],
    [String(data.highlights.length), 'Highlights'],
    [String(data.notes.length), 'Notes'],
    [String(data.bookmarks.length), 'Bookmarks'],
    [
      String(
        data.threadProgress.filter((progress) => progress.completed).length,
      ),
      'Threads completed',
    ],
    [String(data.exploredScriptures.length), 'Scriptures explored'],
  ];
  for (const [value, label] of measures) {
    const item = document.createElement('div');
    const count = document.createElement('strong');
    count.textContent = value;
    const name = document.createElement('span');
    name.textContent = label;
    item.append(count, name);
    stats.append(item);
  }

  const bookmarks = section('Bookmarked talks');
  if (data.bookmarks.length === 0) {
    bookmarks.body.append(empty('Bookmark a talk in the Reader to find it here.'));
  } else {
    const list = document.createElement('ul');
    for (const bookmark of data.bookmarks) {
      const talk = talkById.get(bookmark.talkId);
      const item = document.createElement('li');
      item.append(
        routerLink(`/read/${bookmark.talkId}`, talk?.title ?? bookmark.talkId),
      );
      const speaker = document.createElement('span');
      speaker.textContent = talk?.speaker.name ?? '';
      item.append(speaker);
      list.append(item);
    }
    bookmarks.body.append(list);
  }

  const highlights = section('Highlights');
  if (data.highlights.length === 0) {
    highlights.body.append(empty('Your four-color highlights will appear here.'));
  } else {
    for (const color of Object.keys(HIGHLIGHT_LABELS) as HighlightColor[]) {
      const matching = data.highlights.filter(
        (highlight) => highlight.color === color,
      );
      if (matching.length === 0) continue;
      const group = document.createElement('div');
      group.className = `study-highlight-group study-highlight-group--${color}`;
      const heading = document.createElement('h3');
      heading.textContent = HIGHLIGHT_LABELS[color];
      const list = document.createElement('ul');
      for (const highlight of matching) {
        const item = document.createElement('li');
        const quote = document.createElement('blockquote');
        quote.textContent = highlight.textSnippet;
        const talk = talkById.get(highlight.talkId);
        item.append(
          quote,
          routerLink(
            `/read/${highlight.talkId}/${highlight.paragraphId}`,
            talk?.title ?? highlight.talkId,
          ),
        );
        list.append(item);
      }
      group.append(heading, list);
      highlights.body.append(group);
    }
  }

  const notes = section('Paragraph notes');
  if (data.notes.length === 0) {
    notes.body.append(empty('Notes attached in the Reader will appear here.'));
  } else {
    const list = document.createElement('ul');
    for (const note of data.notes) {
      const item = document.createElement('li');
      const text = document.createElement('p');
      text.textContent = note.noteText;
      const paragraph = note.paragraphId ? `/${note.paragraphId}` : '';
      item.append(
        text,
        routerLink(
          `/read/${note.talkId}${paragraph}`,
          talkById.get(note.talkId)?.title ?? note.talkId,
        ),
      );
      list.append(item);
    }
    notes.body.append(list);
  }

  const progress = section('Reading and thread progress');
  if (
    data.readingProgress.length === 0 &&
    data.threadProgress.length === 0
  ) {
    progress.body.append(empty('Start a talk or thread to begin your journey.'));
  } else {
    const list = document.createElement('ul');
    for (const item of data.readingProgress) {
      const row = document.createElement('li');
      const paragraph = item.paragraphId ? `/${item.paragraphId}` : '';
      row.append(
        routerLink(
          `/read/${item.talkId}${paragraph}`,
          talkById.get(item.talkId)?.title ?? item.talkId,
        ),
        document.createTextNode(
          item.completed
            ? 'Completed'
            : `${String(Math.round(item.percent))}% read`,
        ),
      );
      list.append(row);
    }
    for (const item of data.threadProgress) {
      const row = document.createElement('li');
      row.append(
        routerLink(
          `/threads/${item.threadId}`,
          threadById.get(item.threadId)?.title ?? item.threadId,
        ),
        document.createTextNode(
          item.completed
            ? 'Completed'
            : `${String(item.completedTalkIds.length)} talks completed`,
        ),
      );
      list.append(row);
    }
    progress.body.append(list);
  }

  const scriptures = section('Explored scriptures');
  if (data.exploredScriptures.length === 0) {
    scriptures.body.append(
      empty('Scriptures opened from a published footnote will appear here.'),
    );
  } else {
    const list = document.createElement('ul');
    for (const scripture of data.exploredScriptures) {
      const selection = compactVerseSelection(scripture.verses);
      const item = document.createElement('li');
      item.append(
        routerLink(
          `/scripture/${scripture.bookId}/${String(scripture.chapter)}/${selection}`,
          `${scripture.bookId} ${String(scripture.chapter)}:${selection}`,
        ),
      );
      list.append(item);
    }
    scriptures.body.append(list);
  }
  const grid = document.createElement('div');
  grid.className = 'study-grid';
  grid.append(
    bookmarks.section,
    highlights.section,
    notes.section,
    progress.section,
    scriptures.section,
  );
  page.append(header, stats, grid);
  return page;
}
